import { useState, useEffect, useMemo } from "react";
import { Loader, Plus, CheckSquare, X, Edit2, RotateCcw, ChevronDown, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "../lib/api";
import { fmt, ChangeOrder } from "../lib/utils";
import ConfirmDialog from "./common/ConfirmDialog";
import Sheet from "./common/Sheet";
import KpiCard from "./common/KpiCard";
import RiskDonut from "./common/RiskDonut";

interface ContractRef {
  id: string;
  vendor_name: string;
  contract_value?: number;
}

interface FormState {
  co_number: string;
  description: string;
  amount: string;
  contract_id: string;
}

const EMPTY_FORM: FormState = { co_number: "", description: "", amount: "", contract_id: "" };
const VENDOR_PALETTE = ["#18181B", "#16A34A", "#D97706", "#2563EB", "#6366F1", "#0891B2"];

function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return `${Math.round(n)}`;
}

export default function ChangeOrdersView() {
  const { t } = useTranslation();
  const [projectId, setProjectId] = useState<string | null>(null);
  const [projects, setProjects] = useState<any[]>([]);
  const [contracts, setContracts] = useState<ContractRef[]>([]);
  const [changeOrders, setChangeOrders] = useState<ChangeOrder[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showProjectMenu, setShowProjectMenu] = useState(false);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(null), 4000); };

  const fetchProjects = async () => {
    try {
      const res = await api.get("/projects/");
      const p = res.data.projects;
      setProjects(p);
      if (p.length > 0 && !projectId) { setProjectId(p[0].id); fetchData(p[0].id); }
    } catch {}
  };
  useEffect(() => { fetchProjects(); }, []);

  const fetchData = async (pid?: string) => {
    const id = pid || projectId;
    if (!id) return;
    setLoading(true);
    try {
      const [cosRes, sumRes] = await Promise.all([
        api.get(`/change-orders/project/${id}`),
        api.get(`/change-orders/project/${id}/summary`),
      ]);
      setChangeOrders(cosRes.data.change_orders);
      setSummary(sumRes.data);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!projectId) { setContracts([]); return; }
    const proj = projects.find(p => p.id === projectId);
    const projContracts: ContractRef[] = (proj?.vendors || []).map((v: any) => ({
      id: v.contract_id, vendor_name: v.vendor_name || "Ukjent leverandør", contract_value: v.contract_value,
    }));
    setContracts(projContracts);
  }, [projectId, projects]);

  const openCreateForm = () => { setEditingId(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEditForm = (co: ChangeOrder) => {
    setEditingId(co.id);
    setForm({ co_number: co.co_number, description: co.description, amount: String(co.amount), contract_id: co.contract_id || "" });
    setShowForm(true);
  };

  const submitForm = async () => {
    if (!form.co_number || !form.description || !form.amount || !projectId) return;
    try {
      if (editingId) {
        const res = await api.patch(`/change-orders/${editingId}`, {
          co_number: form.co_number, description: form.description,
          amount: parseFloat(form.amount), contract_id: form.contract_id || null,
        });
        showToast(res.data.needs_reapproval
          ? `Endring lagret. CO satt tilbake til "venter godkjenning". ${res.data.reanalyzed_count} faktura(er) re-analysert.`
          : "Endring lagret.");
      } else {
        await api.post("/change-orders/", {
          project_id: projectId, contract_id: form.contract_id || null,
          co_number: form.co_number, description: form.description,
          amount: parseFloat(form.amount),
          currency: projects.find(p => p.id === projectId)?.currency || "NOK",
          has_documentation: true,
        });
        showToast("Endringsordre opprettet.");
      }
      setForm(EMPTY_FORM); setShowForm(false); setEditingId(null); fetchData();
    } catch (e: any) {
      showToast(`Feil: ${e?.response?.data?.detail ?? "ukjent feil"}`);
    }
  };

  const deleteCO = async (id: string) => {
    setBusyId(id);
    try {
      const res = await api.delete(`/change-orders/${id}`);
      const n = res.data?.reanalyzed_count || 0;
      showToast(n > 0 ? `Slettet. ${n} faktura(er) re-analysert.` : "Slettet.");
      await fetchData();
    } catch (e: any) { showToast(`Feil: ${e?.response?.data?.detail ?? "kunne ikke slette"}`); }
    finally { setDeleteConfirm(null); setBusyId(null); }
  };

  const approveCO = async (id: string) => {
    setBusyId(id);
    try {
      const res = await api.patch(`/change-orders/${id}/approve`, {
        approved_by: "", approved_date: new Date().toISOString().split("T")[0], has_documentation: true,
      });
      const n = res.data.reanalyzed_count || 0;
      showToast(n > 0 ? `Godkjent. ${n} faktura(er) re-analysert.` : "Godkjent.");
      await fetchData();
    } catch (e: any) { showToast(`Feil: ${e?.response?.data?.detail ?? "kunne ikke godkjenne"}`); }
    finally { setBusyId(null); }
  };

  const rejectCO = async (id: string) => {
    setBusyId(id);
    try {
      const res = await api.patch(`/change-orders/${id}/reject`, {});
      const n = res.data?.reanalyzed_count || 0;
      showToast(n > 0 ? `Avvist. ${n} faktura(er) re-analysert.` : "Avvist.");
      await fetchData();
    } catch (e: any) { showToast(`Feil: ${e?.response?.data?.detail ?? "kunne ikke avvise"}`); }
    finally { setBusyId(null); }
  };

  const unrejectCO = async (id: string) => {
    setBusyId(id);
    try { await api.patch(`/change-orders/${id}/unreject`, {}); showToast("Tilbakestilt til 'venter godkjenning'."); await fetchData(); }
    catch (e: any) { showToast(`Feil: ${e?.response?.data?.detail ?? "kunne ikke tilbakestille"}`); }
    finally { setBusyId(null); }
  };

  const unapproveCO = async (id: string) => {
    setBusyId(id);
    const releaseTimer = setTimeout(() => setBusyId(null), 15000);
    try {
      const res = await api.patch(`/change-orders/${id}/unapprove`, {});
      const n = res.data?.reanalyzed_count || 0;
      showToast(n > 0 ? `Godkjenning angret. ${n} faktura(er) re-analysert.` : "Godkjenning angret.");
      await fetchData();
    } catch (e: any) { showToast(`Feil: ${e?.response?.data?.detail ?? e?.message ?? "ukjent feil"}`); }
    finally { clearTimeout(releaseTimer); setBusyId(null); }
  };

  const statusChip = (status: string) => {
    if (status === "approved") return <span className="cx-chip bg-pos/10 text-pos"><span className="w-1.5 h-1.5 rounded-full bg-pos" />{t("common.godkjent")}</span>;
    if (status === "rejected") return <span className="cx-chip bg-neg/10 text-neg"><span className="w-1.5 h-1.5 rounded-full bg-neg" />{t("common.avvist")}</span>;
    return <span className="cx-chip bg-flag/10 text-flag"><span className="w-1.5 h-1.5 rounded-full bg-flag" />{t("changeorder.venter_godkjenning")}</span>;
  };

  // Gruppér CO-er per kontrakt
  const grouped = useMemo(() => {
    const groups = new Map<string, { contract: ContractRef | null; cos: ChangeOrder[] }>();
    for (const co of changeOrders) {
      const key = co.contract_id || "__unassigned__";
      if (!groups.has(key)) {
        const contract = co.contract_id ? contracts.find(c => c.id === co.contract_id) || null : null;
        groups.set(key, { contract, cos: [] });
      }
      groups.get(key)!.cos.push(co);
    }
    return Array.from(groups.entries()).sort(([a], [b]) => (a === "__unassigned__" ? 1 : b === "__unassigned__" ? -1 : 0));
  }, [changeOrders, contracts]);

  const currentProject = projects.find(p => p.id === projectId);
  // Valuta leses ÉN gang fra det valgte prosjektet og sendes inn i hver fmt(...) —
  // aggregat-feltene skal aldri stole på fmt()-defaulten (som er NOK).
  const curr = currentProject?.currency || "NOK";

  const vendorTotals = useMemo(() => {
    const map = new Map<string, { vendor: string; sum: number }>();
    for (const co of changeOrders) {
      if (co.status === "rejected") continue;
      const key = co.contract_id || "__unassigned__";
      const vendor = contracts.find(c => c.id === co.contract_id)?.vendor_name || t("changeorder.uten_kontrakt");
      const existing = map.get(key);
      if (existing) existing.sum += co.amount;
      else map.set(key, { vendor, sum: co.amount });
    }
    return Array.from(map.values()).filter(v => v.sum > 0).sort((a, b) => b.sum - a.sum);
  }, [changeOrders, contracts, t]);
  const totalVendorSum = vendorTotals.reduce((s, v) => s + v.sum, 0);

  return (
    <div className="animate-fade-up">
      {/* Toast */}
      {toast && (
        <div className="fixed top-[64px] right-6 z-[110] bg-night text-white px-4 py-3 rounded-[12px] shadow-pop text-[13px] max-w-md animate-fade-up">
          {toast}
        </div>
      )}

      {deleteConfirm && (
        <ConfirmDialog
          title={t("changeorder.slett_endringsordre")}
          description={t("changeorder.slett_confirm")}
          confirmLabel={t("changeorder.ja_slett")}
          confirmColor="bg-neg hover:opacity-90"
          onConfirm={() => deleteCO(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="text-greeting text-night">{t("changeorder.endringsordrer")}</h1>
          {currentProject && <p className="text-[13.5px] text-muted mt-1">{currentProject.name}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {projects.length > 1 && (
            <div className="relative">
              <button onClick={() => setShowProjectMenu(v => !v)}
                className="flex items-center gap-2 px-3 py-2 bg-canvas border border-line rounded-[10px] text-[13px] font-semibold text-night hover:bg-white transition-colors max-w-[220px]">
                <span className="truncate">{currentProject?.name || t("changeorder.velg_prosjekt")}</span>
                <ChevronDown className={`w-3.5 h-3.5 text-faint shrink-0 transition-transform ${showProjectMenu ? "rotate-180" : ""}`} />
              </button>
              {showProjectMenu && (
                <>
                  <div className="fixed inset-0 z-20" onClick={() => setShowProjectMenu(false)} />
                  <div className="absolute right-0 top-[42px] z-30 w-[240px] bg-card rounded-[12px] shadow-pop border border-line py-1 animate-fade-up overflow-hidden max-h-[300px] overflow-y-auto">
                    {projects.map(p => (
                      <button key={p.id} onClick={() => { setProjectId(p.id); fetchData(p.id); setShowProjectMenu(false); }}
                        className="w-full flex items-center justify-between px-3 py-2.5 text-[13px] text-night hover:bg-black/[0.04] transition-colors text-left">
                        <span className={`truncate ${p.id === projectId ? "font-semibold" : ""}`}>{p.name}</span>
                        {p.id === projectId && <span className="w-1.5 h-1.5 rounded-full bg-night shrink-0 ml-2" />}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          <button onClick={openCreateForm} className="cx-btn-dark">
            <Plus className="w-4 h-4" /> {t("changeorder.ny_endringsordre")}
          </button>
        </div>
      </div>

      {/* KPI-kort */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
          <KpiCard label={t("changeorder.totalt")} value={summary.total_count}
            sub={summary.total_count === 1 ? t("changeorder.endringsordre_singular") : t("changeorder.endringsordrer_plural")} />
          <KpiCard label={t("changeorder.godkjent_verdi")} value={fmt(summary.total_approved_value, curr)}
            sub={t("changeorder.godkjent_count", { count: summary.approved_count })} />
          <KpiCard label={t("changeorder.venter_godkjenning")} value={fmt(summary.total_pending_value, curr)}
            sub={t("changeorder.stk", { count: summary.pending_count })} />
        </div>
      )}

      {/* Eksponering per leverandør */}
      {vendorTotals.length > 0 && (
        <div className="cx-card p-5 mb-5">
          <h3 className="text-[15px] font-bold text-night">{t("changeorder.eksponering_per_leverandor")}</h3>
          <p className="text-[12.5px] text-muted mt-0.5 mb-4">{t("changeorder.innsikt")}</p>
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <RiskDonut size={150} centerLabel={compact(totalVendorSum)} centerSub={curr}
              segments={vendorTotals.map((v, i) => ({ name: v.vendor, value: v.sum, color: VENDOR_PALETTE[i % VENDOR_PALETTE.length] }))} />
            <div className="flex-1 min-w-0 w-full space-y-2">
              {vendorTotals.map((v, i) => {
                const pct = totalVendorSum > 0 ? (v.sum / totalVendorSum) * 100 : 0;
                return (
                  <div key={i} className="flex items-center gap-2.5 text-[13px]">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: VENDOR_PALETTE[i % VENDOR_PALETTE.length] }} />
                    <span className="text-night truncate flex-1 font-medium">{v.vendor}</span>
                    <span className="text-night font-bold tabular shrink-0">{fmt(v.sum, curr)}</span>
                    <span className="text-faint tabular shrink-0 w-10 text-right">{pct.toFixed(0)}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Liste */}
      {changeOrders.length === 0 ? (
        <div className="cx-card p-14 text-center">
          {loading ? (
            <Loader className="w-6 h-6 animate-spin text-faint mx-auto" />
          ) : (
            <>
              <p className="text-[15px] font-bold text-night mb-1">{projectId ? t("changeorder.ingen_endringsordrer") : t("changeorder.velg_prosjekt")}</p>
              <p className="text-[13px] text-muted mb-4">{projectId ? t("changeorder.vis_nar_registrert") : t("changeorder.velg_prosjekt_sub")}</p>
              {projectId && <button onClick={openCreateForm} className="cx-btn-dark mx-auto"><Plus className="w-4 h-4" /> {t("changeorder.ny_endringsordre")}</button>}
            </>
          )}
        </div>
      ) : (
        <div className="space-y-5">
          {grouped.map(([key, group]) => {
            const groupApproved = group.cos.filter(c => c.status === "approved").reduce((s, c) => s + c.amount, 0);
            const groupPending = group.cos.filter(c => c.status === "pending").reduce((s, c) => s + c.amount, 0);
            const contractValue = group.contract?.contract_value || 0;
            const groupTotal = groupApproved + groupPending;
            const exposurePct = contractValue > 0 ? (groupTotal / contractValue) * 100 : 0;
            const apprW = contractValue > 0 ? Math.min((groupApproved / contractValue) * 100, 100) : 0;
            const pendW = contractValue > 0 ? Math.min((groupPending / contractValue) * 100, 100 - apprW) : 0;

            return (
              <div key={key} className="cx-card overflow-hidden">
                {/* Gruppe-header */}
                <div className="px-5 pt-4 pb-4 border-b border-line">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <h3 className="text-[15px] font-bold text-night truncate">
                      {group.contract ? group.contract.vendor_name : t("changeorder.uten_kontrakt")}
                    </h3>
                    {contractValue > 0 && (
                      <p className="text-[12.5px] text-muted tabular shrink-0">
                        <span className="font-bold text-night">{exposurePct.toFixed(1)}%</span> {t("changeorder.av")} {fmt(contractValue, curr)}
                      </p>
                    )}
                  </div>
                  {contractValue > 0 && (
                    <>
                      <div className="h-2 rounded-full bg-black/[0.06] overflow-hidden flex">
                        <div className="h-full bg-pos transition-all duration-700" style={{ width: `${apprW}%` }} />
                        <div className="h-full bg-flag transition-all duration-700" style={{ width: `${pendW}%` }} />
                      </div>
                      <div className="flex items-center justify-between mt-2 text-[12px] tabular">
                        <span className="text-flag font-semibold">{fmt(groupPending, curr)} {t("changeorder.venter_amount")}</span>
                        <span className="text-pos font-semibold">{fmt(groupApproved, curr)} {t("changeorder.godkjent_amount")}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* CO-rader */}
                {group.cos.map((co, idx) => (
                  <div key={co.id} className={`px-4 sm:px-5 py-4 ${idx > 0 ? "border-t border-line" : ""} group hover:bg-canvas transition-colors`}>
                    <div className="flex items-start gap-3 sm:gap-4 flex-wrap sm:flex-nowrap">
                      <div className="shrink-0 pt-0.5">
                        <span className="text-[11px] font-bold uppercase text-faint tabular bg-black/[0.04] px-1.5 py-0.5 rounded-md">{co.co_number}</span>
                      </div>
                      <div className="flex-1 min-w-0 basis-full sm:basis-auto order-3 sm:order-none">
                        <p className="text-[14.5px] font-bold text-night leading-snug mb-1">{co.description}</p>
                        <div className="flex items-center gap-2 flex-wrap">
                          {statusChip(co.status)}
                          {co.approved_by && (
                            <span className="text-[12px] text-faint">{co.approved_by}{co.approved_date && ` · ${new Date(co.approved_date).toLocaleDateString("nb-NO", { day: "numeric", month: "short", year: "numeric" })}`}</span>
                          )}
                          {!co.approved_by && co.created_by_name && (
                            <span className="text-[12px] text-faint">{t("changeorder.laget_av")} {co.created_by_name}{co.created_at && ` · ${new Date(co.created_at).toLocaleDateString("nb-NO", { day: "numeric", month: "short", year: "numeric" })}`}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-auto sm:ml-0">
                        <p className="text-[17px] font-bold text-night tabular leading-none">{fmt(co.amount, co.currency || curr)}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                        {co.status === "pending" && (
                          <>
                            <button onClick={() => approveCO(co.id)} disabled={busyId === co.id}
                              className="w-8 h-8 rounded-[9px] flex items-center justify-center text-pos hover:bg-pos/10 transition-colors disabled:opacity-50" title={t("common.godkjent")}>
                              {busyId === co.id ? <Loader className="w-4 h-4 animate-spin" /> : <CheckSquare className="w-4 h-4" />}
                            </button>
                            <button onClick={() => rejectCO(co.id)} disabled={busyId === co.id}
                              className="w-8 h-8 rounded-[9px] flex items-center justify-center text-neg hover:bg-neg/10 transition-colors disabled:opacity-50" title={t("common.avvist")}>
                              <X className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {co.status === "rejected" && (
                          <button onClick={() => unrejectCO(co.id)} disabled={busyId === co.id}
                            className="w-8 h-8 rounded-[9px] flex items-center justify-center text-muted hover:text-night hover:bg-black/[0.05] transition-colors disabled:opacity-50" title={t("changeorder.tilbakestill")}>
                            {busyId === co.id ? <Loader className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                          </button>
                        )}
                        {co.status === "approved" && (
                          <button onClick={() => unapproveCO(co.id)} disabled={busyId === co.id}
                            className="w-8 h-8 rounded-[9px] flex items-center justify-center text-muted hover:text-flag hover:bg-flag/10 transition-colors disabled:opacity-50" title={t("changeorder.angre_godkjenning")}>
                            {busyId === co.id ? <Loader className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                          </button>
                        )}
                        <button onClick={() => openEditForm(co)} disabled={busyId === co.id}
                          className="w-8 h-8 rounded-[9px] flex items-center justify-center text-faint hover:text-night hover:bg-black/[0.05] transition-colors disabled:opacity-50" title={t("common.rediger")}>
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => setDeleteConfirm(co.id)} disabled={busyId === co.id}
                          className="w-8 h-8 rounded-[9px] flex items-center justify-center text-faint hover:text-neg hover:bg-neg/10 transition-colors disabled:opacity-50" title={t("common.slett")}>
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Legg til CO */}
                <button onClick={() => { openCreateForm(); setForm(prev => ({ ...prev, contract_id: group.contract?.id || "" })); }}
                  className="w-full px-5 py-3.5 border-t border-line flex items-center justify-center gap-2 text-[13.5px] font-semibold text-muted hover:text-night hover:bg-canvas transition-colors">
                  <Plus className="w-4 h-4" /> {t("changeorder.legg_til_endringsordre")}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal: Ny / rediger CO */}
      <Sheet open={showForm} onClose={() => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); }}
        title={editingId ? t("changeorder.rediger_endringsordre") : t("changeorder.ny_endringsordre")}
        subtitle={currentProject?.name}>
        <div className="space-y-4">
          {editingId && (
            <div className="rounded-[10px] p-3 text-[12px] bg-flag/10 text-flag font-medium">
              {t("changeorder.reapproval_warning")}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("changeorder.co_nummer")}</label>
              <input value={form.co_number} onChange={e => setForm({ ...form, co_number: e.target.value })} placeholder="CO-006" className="field-ios" autoFocus />
            </div>
            <div>
              <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("changeorder.belop")} ({currentProject?.currency || "NOK"})</label>
              <input value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="25000" type="number" className="field-ios" />
            </div>
          </div>
          <div>
            <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("invoice.leverandor")}</label>
            <select value={form.contract_id} onChange={e => setForm({ ...form, contract_id: e.target.value })} className="field-ios">
              <option value="">— {t("changeorder.uten_kontrakt")} —</option>
              {contracts.map(c => <option key={c.id} value={c.id}>{c.vendor_name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[12px] font-semibold text-muted mb-1.5 block">{t("changeorder.beskrivelse")}</label>
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder={t("changeorder.beskriv_endringsarbeidet")} rows={3} className="field-ios resize-none" />
          </div>
          <div className="flex items-start gap-2 rounded-[10px] p-3 bg-canvas border border-line">
            <Lock className="w-3.5 h-3.5 text-faint mt-0.5 shrink-0" />
            <p className="text-[11.5px] text-muted leading-relaxed">{t("changeorder.godkjenning_info")}</p>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-4 mt-1 border-t border-line -mx-6 px-6">
            <button onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); }} className="cx-btn-ghost !px-5 !py-2.5">
              {t("common.avbryt")}
            </button>
            <button onClick={submitForm} disabled={!form.co_number || !form.description || !form.amount} className="cx-btn-dark !px-5 !py-2.5">
              {editingId ? t("common.lagre_endringer") : t("changeorder.lagre_endringsordre")}
            </button>
          </div>
        </div>
      </Sheet>
    </div>
  );
}
